'use client';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';

import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';
import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';

import { Form, FormField } from '@eliadmin/core/src/components/ui/form';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { Button } from '../ui/button';
import { Heading } from '../ui/heading';
import { InputField, SelectField } from '../ui/input-field';

/**
 * Karta „Cenová ponuka do 24 hodín" v heri — Figma `60042:5211`.
 *
 * Napojenie kopíruje `components/contact/contact-form.tsx` z jadra: zod schéma
 * cez `useFormValidationMessages`, `useForm` + `zodResolver`, odoslanie cez
 * `useMutation(sendContactMessage)` na `/store/forms/contact`, reCAPTCHA v3,
 * meranie cez `trackFormSubmitSuccess`, hlásenia cez `toast` a rozpad 422 do
 * polí cez `ValidationError`.
 *
 * Schému zadal David vrátane kľúča `custom.text1139` pre „Mám záujem o".
 *
 * Na desktope sú telefón a e-mail vedľa seba, na mobile pod sebou (Figma).
 */

const heroContactSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    phone: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    // „Mám záujem o" — David určil kľúč `text1139` v objekte `custom`.
    custom: z.object({
      text1139: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    }),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    attachments: z.array(z.object({ id: z.number() })).optional(),
    'g-recaptcha-response': z.string().default(''),
  });

type HeroFormInput = z.input<ReturnType<typeof heroContactSchema>>;
type HeroFormValues = z.output<ReturnType<typeof heroContactSchema>>;

/** TODO(CMS): nahradiť zoznamom z modulu Služby. */
const SERVICE_OPTIONS = [
  { value: 'Expresná preprava', label: 'Expresná preprava' },
  { value: 'Medzinárodná preprava', label: 'Medzinárodná preprava' },
  { value: 'Skladovanie', label: 'Skladovanie' },
  { value: 'Iné', label: 'Iné' },
];

export function HeroQuoteForm() {
  const { t } = useTranslations();
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<HeroFormInput, any, HeroFormValues>({
    resolver: zodResolver(heroContactSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      custom: { text1139: '' },
      gdpr: true,
      attachments: [],
      'g-recaptcha-response': '',
    },
  });

  const { mutateAsync } = useMutation({ mutationFn: sendContactMessage });

  const handleSubmit = form.handleSubmit(async data => {
    try {

      await mutateAsync({ data: { ...data,  }, locale });

      trackFormSubmitSuccess({
        form_id: 'hero-quote',
        form_name: 'hero_quote_form',
        form_destination: typeof window !== 'undefined' ? window.location.href : undefined,
        form_length: Object.keys(data).length,
        send_to: generalLayout.marketing?.gtm_id || '',
      });

      toast({ title: t('text658'), variant: 'success' });
      form.reset();
      router.push(ROUTES.THANK_YOU());
    } catch (err) {
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }
      toast({ title: t('text657'), description: (err as any)?.message, variant: 'destructive' });
    }
  });

  return (
    <Form {...form}>
        <CaptchaFormMessage errors={form.formState.errors} />
      <form
        onSubmit={handleSubmit}
        className={cn(
          'ea-hero-quote-form',
          'relative z-[5] flex w-full flex-col gap-4 rounded-16 bg-[#FFFFFF] p-5',
          'md:gap-5 md:p-6 xl:gap-6 xl:p-8',
        )}
      >
        <Heading tag="h5" as="h2" title="Cenová ponuka do 24 hodín" />

        <div className="flex flex-col gap-3">
          <FormField
            control={form.control}
            name="name"
            render={({ field, fieldState }) => (
              <InputField
                {...field}
                label="Meno a priezvisko / Firma"
                placeholder="Martin Novák"
                required
                error={fieldState.error?.message}
              />
            )}
          />

          <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
            <FormField
              control={form.control}
              name="phone"
              render={({ field, fieldState }) => (
                <InputField
                  {...field}
                  label="Telefón"
                  type="tel"
                  placeholder="+421..."
                  required
                  error={fieldState.error?.message}
                />
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState }) => (
                <InputField
                  {...field}
                  label="E-mail"
                  type="email"
                  placeholder="novak@email.com"
                  required
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="custom.text1139"
            render={({ field, fieldState }) => (
              <SelectField
                {...field}
                label="Mám záujem o"
                placeholder="Vyberte službu"
                options={SERVICE_OPTIONS}
                required
                error={fieldState.error?.message}
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <p className="max-w-[320px] text-[12px] leading-[1.43] tracking-[-0.036em] text-[#55617A] sm:text-[13px] lg:text-[14px]">
            Vyplňte zrýchlený formulár a my sa Vám ozveme späť, spravidla do 24 hodín.
          </p>
          <Button
            type="submit"
            variant="brand-accent"
            size="brand"
            rounded="pill"
            isLoading={form.formState.isSubmitting}
            className="w-full shrink-0 sm:w-auto"
          >
            Odoslať
          </Button>
        </div>
      </form>
    </Form>
  );
}
